/*
  <cart-upsell> — recommendation rail for the cart drawer and cart page.

  The theme already ships <product-complimentary> (theme.js) for the PDP, but it
  fetches once on connect and never looks at the cart. This one is cart-aware:
  it re-anchors when the cart changes and drops anything the shopper already has.

  Markup comes from sections/api-cart-upsell.liquid so the cards stay the theme's
  own product-clip snippet — one-click add and quick-view keep working untouched.
*/
(function () {
  'use strict';

  if (window.customElements && customElements.get('cart-upsell')) return;

  var CART_EVENTS = ['theme:cart:change', 'theme:cart:reload'];
  var DEBOUNCE_MS = 250;

  /*
    Ask for more cards than we display: some come back already in the cart and
    get filtered out, and a short response would otherwise leave gaps in the row.
  */
  var OVERFETCH = 4;
  var MAX_FETCH = 10;

  var CartUpsell = class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.items = null;
      this.seedId = null;
      this.timer = null;
      this.onCartEvent = this.onCartEvent.bind(this);
    }

    connectedCallback() {
      this.holder = this.closest('[data-cart-upsell-holder]');
      this.list = this.querySelector('[data-cart-upsell-list]');
      if (!this.list) return;

      this.limit = parseInt(this.dataset.limit, 10) || 2;
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.recUrl = this.dataset.recommendationsUrl || '/recommendations/products';
      this.sectionId = this.dataset.sectionId || 'api-cart-upsell';

      /*
        Threshold arrives in whole currency units from settings.free_shipping_limit,
        and is 0 whenever this surface isn't showing the free shipping message.
      */
      this.shippingLimit = parseFloat(this.dataset.freeShippingLimit) || 0;
      this.shippingBadge = this.dataset.shippingBadge || '';

      CART_EVENTS.forEach(function (name) {
        document.addEventListener(name, this.onCartEvent);
      }, this);

      this.refresh();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      CART_EVENTS.forEach(function (name) {
        document.removeEventListener(name, this.onCartEvent);
      }, this);
    }

    onCartEvent() {
      /*
        Both events can fire several times for a single add (the theme's own
        add-to-cart, then rebuy-cart-sync replaying it), so coalesce them.
      */
      clearTimeout(this.timer);
      this.timer = setTimeout(this.refresh.bind(this), DEBOUNCE_MS);
    }

    refresh() {
      var self = this;

      return fetch(this.cartUrl + '.js', {
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          var seedId = self.pickSeed(cart);
          if (!seedId) {
            self.seedId = null;
            self.items = null;
            self.hide();
            return;
          }

          var cartIds = (cart.items || []).map(function (item) {
            return String(item.product_id);
          });
          var cartTotal = cart.total_price || 0;

          /*
            The server filters the cards against the cart (pregnancy/breastfeeding
            rule in api-cart-upsell.liquid), so the markup is only reusable while
            the cart holds the same products. Re-fetch when the seed OR the set of
            products changes; a plain quantity change still skips the round trip.
          */
          var cartSig = cartIds.slice().sort().join('-');
          if (self.items && self.seedId === seedId && self.cartSig === cartSig) {
            self.render(cartIds, cartTotal);
            return;
          }

          self.seedId = seedId;
          self.cartSig = cartSig;
          return self.fetchItems(seedId).then(function (nodes) {
            self.items = nodes;
            self.render(cartIds, cartTotal);
          });
        })
        .catch(function (error) {
          console.warn('[cart-upsell] refresh failed', error);
          self.hide();
        });
    }

    /*
      Anchor on the highest-value line. A complementary offer reads best against
      the hero product, which is rarely whatever happened to be added first.
    */
    pickSeed(cart) {
      if (!cart || !cart.items || !cart.items.length) return null;

      var best = null;
      cart.items.forEach(function (item) {
        if (!item.product_id) return;
        if (!best || item.final_line_price > best.final_line_price) best = item;
      });

      return best ? String(best.product_id) : null;
    }

    fetchItems(seedId) {
      var self = this;

      return this.request(seedId, 'complementary').then(function (nodes) {
        if (nodes.length) return nodes;
        /*
          Complementary products only exist once a merchant has paired them in
          Search & Discovery. Fall back to Shopify's automatic related products
          so the rail still has something to show before that work is done.
        */
        return self.request(seedId, 'related');
      });
    }

    request(seedId, intent) {
      var limit = Math.min(this.limit + OVERFETCH, MAX_FETCH);
      var url = this.recUrl +
        '?section_id=' + encodeURIComponent(this.sectionId) +
        '&product_id=' + encodeURIComponent(seedId) +
        '&intent=' + encodeURIComponent(intent) +
        '&limit=' + limit +
        /* Not read by Shopify; makes the cache key cart-specific so the Liquid
           cart filter in api-cart-upsell.liquid always sees the live cart. */
        '&cart=' + encodeURIComponent(this.cartSig || '');

      return fetch(url, { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (text) {
          var parsed = document.createElement('div');
          parsed.innerHTML = text;

          var content = parsed.querySelector('[data-api-content]');
          if (!content) return [];

          return Array.prototype.slice.call(
            content.querySelectorAll('[data-upsell-product-id]')
          );
        })
        .catch(function () { return []; });
    }

    /*
      Cents still needed to reach free shipping, or 0 when there's no threshold,
      no promise to make, or the cart already qualifies.

      cart.total_price comes back in the presentment currency while the prices
      Liquid wrote into the markup are in the shop currency, so the shop-side
      numbers get converted — the approach cart-shipping-message.liquid documents.
    */
    shippingGap(cartTotal) {
      if (!this.shippingLimit || !this.shippingBadge) return 0;

      var limit = this.shippingLimit * 100 * this.currencyRate();
      if (cartTotal >= limit) return 0;

      return limit - cartTotal;
    }

    currencyRate() {
      var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
      return rate > 0 ? rate : 1;
    }

    closesGap(node, gap) {
      if (!gap) return false;
      var price = parseInt(node.getAttribute('data-upsell-price'), 10);
      if (!price) return false;
      return price * this.currencyRate() >= gap;
    }

    render(cartIds, cartTotal) {
      if (!this.items || !this.items.length) {
        this.list.innerHTML = '';
        this.hide();
        return;
      }

      var gap = this.shippingGap(cartTotal || 0);

      /*
        While she's short of free shipping, the cards that actually close the gap
        lead the rail — the offer that solves her problem shouldn't be the one she
        has to scroll to. Stable partition, so the merchant's ordering survives
        within each group.
      */
      var ordered = this.items;
      if (gap) {
        var closes = [];
        var rest = [];
        this.items.forEach(function (node) {
          (this.closesGap(node, gap) ? closes : rest).push(node);
        }, this);
        ordered = closes.concat(rest);
      }

      var fragment = document.createDocumentFragment();
      var shown = 0;

      ordered.forEach(function (node) {
        if (shown >= this.limit) return;
        if (cartIds.indexOf(node.getAttribute('data-upsell-product-id')) !== -1) return;

        /*
          Clone rather than move: this.items is the cached master list, and the
          nodes get re-rendered every time the cart changes. The originals live
          in a detached element, so Alpine only initialises the copies we append.
        */
        var card = node.cloneNode(true);
        if (this.closesGap(card, gap)) this.addBadge(card);

        fragment.appendChild(card);
        shown += 1;
      }, this);

      this.list.innerHTML = '';

      if (!shown) {
        this.hide();
        return;
      }

      this.list.appendChild(fragment);
      this.show();
    }

    /*
      Turns "spend R600.05 more" into a per-card fact: adding this one is what
      makes delivery free.
    */
    addBadge(card) {
      var content = card.querySelector('.product-clip__content');
      if (!content || card.querySelector('.cart-upsell__badge')) return;

      var badge = document.createElement('span');
      badge.className = 'cart-upsell__badge';
      badge.textContent = this.shippingBadge;
      content.insertBefore(badge, content.firstChild);
    }

    show() {
      if (this.holder) this.holder.classList.remove('js-unloaded');
    }

    hide() {
      if (this.holder) this.holder.classList.add('js-unloaded');
    }
  };

  customElements.define('cart-upsell', CartUpsell);
})();
